import Vue from "vue";
import VueRouter from "vue-router";

// Libraries
import { auth } from "@/firebase/init";
import store from "@/store/index.js";

const originalPush = VueRouter.prototype.push;
const { isNavigationFailure, NavigationFailureType } = VueRouter;

VueRouter.prototype.push = function (location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }
  return originalPush.call(this, location).catch((err) => {
    if (VueRouter.isNavigationFailure(err)) {
      // resolve err
      return err;
    }
    // rethrow error
    return Promise.reject(err);
  });
};

Vue.use(VueRouter);

const routes = [
  {
    path: "*",
    redirect: "/404",
    component: () => import("@/components/NotFound.vue"),
    meta: {
      trail: () => [{ text: "Home", to: "/" }, { text: "Not Found" }],
    },
  },
  {
    path: "/403",
    name: "403",
    component: () => import("@/components/NotAllowed.vue"),
    meta: {
      trail: () => [{ text: "Home", to: "/" }, { text: "Not Allowed" }],
    },
  },
  {
    path: "/404",
    name: "404",
    component: () => import("@/components/NotFound.vue"),
    meta: {
      trail: () => [{ text: "Home", to: "/" }, { text: "Not Found" }],
    },
  },
  {
    path: "/",
    name: "home",
    component: () => import("@/views/landing/Home.vue"),
    meta: {
      requiresAuth: true,
      text: "Home",
      trail: () => [{ text: "Home" }],
    },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/landing/Login.vue"),
    meta: {
      trail: () => [],
    },
  },
  {
    path: "/buyer/:id",
    name: "salesBuyer-order",
    component: () => import("@/components/misc/dashboards/vendors/Sales.vue"),
    meta: {
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Buyers", to: "/buyers" },
        { text: `Buyer` },
      ],
    },
  },
  {
    path: "/creditRequest/:id",
    name: "creditRequest-submit",
    component: () => import("@/components/misc/CreditRequest.vue"),
    meta: {
      trail: (route) => [],
    },
  },
  /* Phase 0 - Admin */
  // User MGMT
  {
    path: "/users/overview",
    name: "users",
    component: () => import("@/views/phases/0_admin/users/Users_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Users: Overview",
      trail: () => [{ text: "Home", to: "/" }, { text: "Users: Overview" }],
    },
  },
  {
    path: "/users/settings",
    name: "users-settings",
    component: () => import("@/views/phases/0_admin/users/Users_Settings.vue"),
    meta: {
      requiresAuth: true,
      text: "Roles Configuration",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Users", to: "/users/overview" },
        { text: "Roles Configuration" },
      ],
    },
  },
  {
    path: "/user/new",
    name: "user-new",
    component: () => import("@/views/phases/0_admin/users/User_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New User",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Users", to: "/users/overview" },
        { text: "New User" },
      ],
    },
  },
  {
    path: "/user/:id",
    name: "user-view",
    component: () => import("@/views/phases/0_admin/users/User_View.vue"),
    meta: {
      requiresAuth: true,
      text: "User Profile",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Users", to: "/users/overview" },
        { text: `User` },
      ],
    },
  },
  {
    path: "/profile/:id",
    name: "profile-view",
    component: () => import("@/components/misc/UserProfile.vue"),
    meta: {
      requiresAuth: true,
      text: "Profile",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Profile", to: "/profile" },
        { text: `User` },
      ],
    },
  },
  {
    path: "/promotionCredit/:id",
    name: "promotionCredit-view",
    component: () => import("@/components/misc/PromotionCredit.vue"),
    meta: {
      requiresAuth: true,
      text: "Promotion Credit",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: `Promotion Credit`, to: "/promotionCredit/:id" },
      ],
    },
  },
  {
    path: "/__/auth/handler",
    name: "oAuth-handler",
    component: () => import("@/components/misc/OAuthHandler.vue"),
    meta: {
      trail: () => [{ text: "Home", to: "/" }, { text: "Auth Handler" }],
    },
  },
  {
    path: "/__/auth/quickbooks/handler",
    name: "quickbooks-oAuth-handler",
    component: () => import("@/components/misc/QuickbooksOAuthHandler.vue"),
    meta: {
      trail: () => [{ text: "Home", to: "/" }, { text: "Auth Handler" }],
    },
  },
  // Data Management
  {
    path: "/dataManagement/overview",
    name: "dataManagement-overview",
    component: () =>
      import("@/views/phases/0_admin/dataManagement/DataImport_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Data Management",
      trail: () => [{ text: "Home", to: "/" }, { text: "Data Management" }],
    },
  },
  {
    path: "/dataManagement/mytrace/instance",
    name: "dataManagement-mytrace-instance",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_Instance.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Instance Data",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Instance Export/Import" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/import",
    name: "dataManagement-mytrace-import",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_Import.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Import",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Import" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/accounts",
    name: "dataManagement-mytrace-account",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataImport_MyTrace_AccountMerge.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Account Merge",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Account Merge" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/account-transfer",
    name: "dataManagement-mytrace-account-transfer",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataImport_MyTrace_AccountTransfer.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Account Transfer",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Account Transfer" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/account-export",
    name: "dataManagement-mytrace-account-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataImport_MyTrace_AccountExport.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Account Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Account Export" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/lineItems-export",
    name: "dataManagement-mytrace-lineitems-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_LineItemsExport.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Line Items Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Line Items Export" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/store-events-export",
    name: "dataManagement-mytrace-store-events-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_StoreEventsExport.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Store Events Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Store Events Export" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/trainings-export",
    name: "dataManagement-mytrace-trainings-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_trainingsExport.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Trainings Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Trainings Export" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/inventory-export",
    name: "dataManagement-mytrace-inventory-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_InventoryExport.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Inventory Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Inventory Export" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/rawMaterials-export",
    name: "dataManagement-mytrace-rawMaterials-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_RawMaterials.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Raw Materials Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Raw Materials, Export" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/components-export",
    name: "dataManagement-mytrace-components-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_Components.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Components Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Components, Export" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/facilities-export",
    name: "dataManagement-mytrace-facilities-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_Facilities.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Facility Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Facility Export" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/brandStats",
    name: "dataManagement-mytrace-brandStats",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_BrandStats.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Brand Stats",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Brand Stats" },
      ],
    },
  },

  {
    path: "/dataManagement/mytrace/retag",
    name: "dataManagement-mytrace-retag",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataImport_MyTrace_Retag.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Retag Records",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Retag Records" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/recalculate",
    name: "dataManagement-mytrace-recalculate",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataMGMT_MyTrace_Recalculate.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Recalculate",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Recalculate" },
      ],
    },
  },
  {
    path: "/dataManagement/mytrace/socials",
    name: "dataManagement-mytrace-socials",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/mytrace/DataImport_MyTrace_Socials.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "MyTrace: Socials",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "MyTrace: Socials Data" },
      ],
    },
  },
  {
    path: "/dataManagement/apextrading/accounts",
    name: "dataManagement-apextrading-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/apextrading/DataImport_ApexTrading_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Apex Trading: Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Apex Trading Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/apextrading/products",
    name: "dataManagement-apextrading-products",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/apextrading/DataMGMT_ApexTrading_Products.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Apex Trading: Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Apex Trading Products" },
      ],
    },
  },
  {
    path: "/dataManagement/apextrading/salesOrders",
    name: "dataManagement-apextrading-salesOrders",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/apextrading/DataImport_ApexTrading_SalesOrders.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Apex Trading: Sales Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Apex Trading Sales Orders" },
      ],
    },
  },
  {
    path: "/dataManagement/canix/products",
    name: "dataManagement-canix-products",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/canix/DataMGMT_Canix_Products.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Canix: Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Canix Products" },
      ],
    },
  },
  {
    path: "/dataManagement/canix/accounts",
    name: "dataManagement-canix-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/canix/DataMGMT_Canix_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Canix: Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Canix Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/canix/salesOrders",
    name: "dataManagement-canix-salesOrders",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/canix/DataMGMT_Canix_SalesOrders.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Canix: Sales Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Canix: Sales Orders" },
      ],
    },
  },
  {
    path: "/dataManagement/leaflink/sync",
    name: "dataManagement-leaflink-sync",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/leaflink/DataImport_LeafLink_Sync.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink: Account Sync",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "LeafLink: Account Sync" },
      ],
    },
  },
  {
    path: "/dataManagement/leaflink/inventory",
    name: "dataManagement-leaflink-inventory",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/leaflink/DataMGMT_LeafLink_Inventory.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink: Inventory",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "LeafLink: Inventory" },
      ],
    },
  },
  {
    path: "/dataManagement/leaflink/productLines",
    name: "dataManagement-leaflink-productLines",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/leaflink/DataMGMT_LeafLink_ProductLines.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink: Product Lines",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "LeafLink: Product Lines" },
      ],
    },
  },
  {
    path: "/dataManagement/leaflink/products",
    name: "dataManagement-leaflink-products",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/leaflink/DataImport_LeafLink_Products.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink: Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "LeafLink: Products" },
      ],
    },
  },
  {
    path: "/dataManagement/leaflink/accounts",
    name: "dataManagement-leaflink-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/leaflink/DataImport_LeafLink_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink: Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "LeafLink: Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/leaflink/contacts",
    name: "dataManagement-leaflink-contacts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/leaflink/DataImport_LeafLink_Contacts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink Contacts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "LeafLink: Contacts" },
      ],
    },
  },
  {
    path: "/dataManagement/leaflink/salesOrders",
    name: "dataManagement-leaflink-salesOrders",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/leaflink/DataMGMT_LeafLink_Orders.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink Sales Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "LeafLink: Sales Orders" },
      ],
    },
  },
  {
    path: "/dataManagement/leaflink/refreshOrders",
    name: "dataMGMT-leaflink-refreshOrders",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/leaflink/DataMGMT_LeafLink_RefreshOrders.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink Refresh Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "LeafLink: Refresh Orders" },
      ],
    },
  },
  {
    path: "/dataManagement/leaflink/sage",
    name: "dataManagement-leaflink-sage",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/leaflink/DataMGMT_LeafLink_Sage.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink Sage export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "LeafLink: Sage Export" },
      ],
    },
  },
  {
    path: "/dataManagement/distru/import",
    name: "dataManagement-distru-import",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/distru/DataMGMT_Distru_Import.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Distru: Import CSVs",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Distru: Import CSVs" },
      ],
    },
  },
  {
    path: "/dataManagement/distru/sync-products",
    name: "dataManagement-distru-syncProducts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/distru/DataMGMT_Distru_SyncProducts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Distru: Sync Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Distru: Sync Products" },
      ],
    },
  },
  {
    path: "/dataManagement/distru/add-products",
    name: "dataManagement-distru-addProducts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/distru/DataMGMT_Distru_AddProducts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Distru: Add Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Distru: Add Products" },
      ],
    },
  },
  {
    path: "/dataManagement/distru/accounts",
    name: "dataManagement-distru-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/distru/DataMGMT_Distru_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Distru: Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Distru: Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/distru/contacts",
    name: "dataManagement-distru-contacts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/distru/DataMGMT_Distru_Contacts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Distru: Contacts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Distru: Contacts" },
      ],
    },
  },
  {
    path: "/dataManagement/distru/salesOrders",
    name: "dataManagement-distru-salesOrders",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/distru/DataMGMT_Distru_Orders.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Distru: Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Distru: Orders" },
      ],
    },
  },
  {
    path: "/dataManagement/nabis/import",
    name: "dataManagement-nabis-import",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/nabis/DataMGMT_Nabis_Import.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Nabis: Import",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Nabis: Import" },
      ],
    },
  },
  {
    path: "/dataManagement/nabis/etl",
    name: "dataManagement-nabis-etl",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/nabis/DataMGMT_Nabis_ETL.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Nabis: ETL",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Nabis: ETL" },
      ],
    },
  },
  {
    path: "/dataManagement/nabis/accounts",
    name: "dataManagement-nabis-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/nabis/DataMGMT_Nabis_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Nabis: Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Nabis: Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/nabis/products",
    name: "dataManagement-nabis-products",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/nabis/DataMGMT_Nabis_Products.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Nabis: Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Nabis: Products" },
      ],
    },
  },
  {
    path: "/dataManagement/nabis/salesOrders",
    name: "dataManagement-nabis-salesOrders",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/nabis/DataMGMT_Nabis_SalesOrders.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Nabis: Sales Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Nabis: Sales Orders" },
      ],
    },
  },
  {
    path: "/dataManagement/quickbooks/config",
    name: "dataManagement-quickbooks-config",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/quickbooks/DataMGMT_Quickbooks_Config.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Quickbooks Config",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Quickbooks: Config" },
      ],
    },
  },
  {
    path: "/dataManagement/quickbooks/account-sync",
    name: "datamgmt-quickbooks-account-sync",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/quickbooks/DataMGMT_Quickbooks_AccountSync.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Quickbooks Account Sync",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Quickbooks: Account Sync" },
      ],
    },
  },
  // {
  //   path: "/dataManagement/quickbooks/products",
  //   name: "dataManagement-quickbooks-products",
  //   component: () =>
  //     import(
  //       "@/views/phases/0_admin/dataManagement/quickbooks/DataMGMT_Quickbooks_Products.vue"
  //     ),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Quickbooks Products",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Data MGMT", to: "/dataManagement/overview" },
  //       { text: "Quickbooks: Products" },
  //     ],
  //   },
  // },
  {
    path: "/dataManagement/quickbooks/export",
    name: "dataManagement-quickbooks-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/quickbooks/DataMGMT_Quickbooks_Export.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Quickbooks Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Quickbooks: Export" },
      ],
    },
  },
  {
    path: "/dataManagement/zoho/accounts",
    name: "dataManagement-zoho-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/zoho/DataImport_Zoho_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Zoho Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Zoho: Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/zoho/contacts",
    name: "dataManagement-zoho-contacts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/zoho/DataImport_Zoho_Contacts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Zoho Contacts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Zoho: Contacts" },
      ],
    },
  },
  {
    path: "/dataManagement/flourish/accounts",
    name: "dataManagement-flourish-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/flourish/DataMGMT_Flourish_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Flourish Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Flourish: Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/flourish/products",
    name: "dataManagement-flourish-products",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/flourish/DataMGMT_Flourish_Products.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Flourish Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Flourish: Products" },
      ],
    },
  },
  {
    path: "/dataManagement/flourish/orders",
    name: "dataManagement-flourish-orders",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/flourish/DataMGMT_Flourish_Orders.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Flourish Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Flourish: Orders" },
      ],
    },
  },
  {
    path: "/dataManagement/metrc/accounts",
    name: "dataManagement-metrc-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/metrc/DataMGMT_Metrc_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Metrc Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Metrc: Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/metrc/items",
    name: "dataManagement-metrc-items",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/metrc/DataMGMT_Metrc_Items.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Metrc Items",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Metrc: Map Products" },
      ],
    },
  },
  {
    path: "/dataManagement/metrc/inventory",
    name: "dataManagement-metrc-inventory",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/metrc/DataMGMT_Metrc_Inventory.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Metrc Sync Inventory",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Metrc: Sync Inventory" },
      ],
    },
  },
  {
    path: "/dataManagement/metrc/rawMaterials",
    name: "dataManagement-metrc-rawMaterials",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/metrc/DataMGMT_Metrc_RawMaterials.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Metrc Raw Materials",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Metrc: Raw Materials" },
      ],
    },
  },
  {
    path: "/dataManagement/metrc/salesOrders",
    name: "dataManagement-metrc-salesOrders",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/metrc/DataMGMT_Metrc_Manifests.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Metrc Manifests",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Metrc: Manifests" },
      ],
    },
  },
  {
    path: "/dataManagement/hoodie/accounts",
    name: "dataManagement-hoodie-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/hoodie/DataMGMT_Hoodie_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Hoodie Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Hoodie: Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/hoodie/products",
    name: "dataManagement-hoodie-products",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/hoodie/DataMGMT_Hoodie_Products.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Hoodie Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Hoodie: Products" },
      ],
    },
  },
  {
    path: "/dataManagement/hoodie/menus",
    name: "dataManagement-hoodie-menus",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/hoodie/DataMGMT_Hoodie_Menus.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Hoodie Menu",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Hoodie: Menu" },
      ],
    },
  },
  {
    path: "/dataManagement/method/users",
    name: "dataManagement-method-users",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/method/DataMGMT_Method_Users.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Method: Users",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Method: Users" },
      ],
    },
  },
  {
    path: "/dataManagement/method/customers",
    name: "dataManagement-method-customers",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/method/DataMGMT_Method_Customers.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Method: Customers",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Method: Customers" },
      ],
    },
  },
  {
    path: "/dataManagement/method/contacts",
    name: "dataManagement-method-contacts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/method/DataMGMT_Method_Contacts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Method: Contacts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Method: Contacts" },
      ],
    },
  },
  {
    path: "/dataManagement/method/tasks",
    name: "dataManagement-method-tasks",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/method/DataMGMT_Method_Tasks.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Method: Tasks",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Method: Tasks" },
      ],
    },
  },
  {
    path: "/dataManagement/method/items",
    name: "dataManagement-method-items",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/method/DataMGMT_Method_Items.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Method: Items",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Method: Items" },
      ],
    },
  },
  {
    path: "/dataManagement/method/invoices",
    name: "dataManagement-method-invoices",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/method/DataMGMT_Method_Invoices.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Method: Invoices",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Method: Invoices" },
      ],
    },
  },
  {
    path: "/dataManagement/intacct/productLines",
    name: "dataManagement-intacct-productLines",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/intacct/DataMGMT_Intacct_ProductLines.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Intacct Product Lines",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Intacct: Product Lines" },
      ],
    },
  },
  {
    path: "/dataManagement/intacct/accounts",
    name: "dataManagement-intacct-syncAccounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/intacct/DataMGMT_Intacct_SyncAccounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Intacct Sync Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Intacct: Sync Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/intacct/products",
    name: "dataManagement-intacct-products",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/intacct/DataMGMT_Intacct_Products.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Intacct Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Intacct: Products" },
      ],
    },
  },
  {
    path: "/dataManagement/intacct/export",
    name: "dataManagement-intacct-export",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/intacct/DataMGMT_Intacct_Export.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Intacct Export",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Intacct: Export" },
      ],
    },
  },
  {
    path: "/dataManagement/monday/config",
    name: "dataManagement-monday-config",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/monday/DataMGMT_Monday_Config.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Monday Config",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Monday: Config" },
      ],
    },
  },
  {
    path: "/dataManagement/monday/accounts",
    name: "dataManagement-monday-accounts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/monday/DataMGMT_Monday_Accounts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Monday Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Monday: Accounts" },
      ],
    },
  },
  {
    path: "/dataManagement/monday/contacts",
    name: "dataManagement-monday-contacts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/monday/DataMGMT_Monday_Contacts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Monday Contacts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Monday: Contacts" },
      ],
    },
  },
  {
    path: "/dataManagement/hubspot/companies",
    name: "dataManagement-hubspot-companies",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/hubspot/DataMGMT_Hubspot_Companies.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Hubspot Companies",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Hubspot: Companies" },
      ],
    },
  },
  {
    path: "/dataManagement/hubspot/contacts",
    name: "dataManagement-hubspot-contacts",
    component: () =>
      import(
        "@/views/phases/0_admin/dataManagement/hubspot/DataMGMT_Hubspot_Contacts.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Hubspot Contacts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Data MGMT", to: "/dataManagement/overview" },
        { text: "Hubspot: Contacts" },
      ],
    },
  },
  // Integrations
  {
    path: "/integrations/overview",
    name: "integrations-overview",
    component: () =>
      import("@/views/phases/0_admin/integrations/Integrations_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Integrations",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Integrations: Overview" },
      ],
    },
  },
  // Operating Zones
  {
    path: "/operatingZones/overview",
    name: "operatingZones-overview",
    component: () =>
      import(
        "@/views/phases/0_admin/operatingZones/OperatingZones_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Operating Zones: Overview",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Operating Zones: Overview" },
      ],
    },
  },
  {
    path: "/country/new",
    name: "country-new",
    component: () =>
      import("@/views/phases/0_admin/operatingZones/countries/Country_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Country",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Operating Zones", to: "/operatingZones/overview" },
        { text: "New Country" },
      ],
    },
  },
  {
    path: "/country/:id",
    name: "country-view",
    component: () =>
      import(
        "@/views/phases/0_admin/operatingZones/countries/Country_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Country",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Operating Zones", to: "/operatingZones/overview" },
        { text: `Country` },
      ],
    },
  },
  {
    path: "/state/new",
    name: "state-new",
    component: () =>
      import("@/views/phases/0_admin/operatingZones/states/State_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New State",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Operating Zones", to: "/operatingZones/overview" },
        { text: "New State" },
      ],
    },
  },
  {
    path: "/state/:id",
    name: "state-view",
    component: () =>
      import("@/views/phases/0_admin/operatingZones/states/State_View.vue"),
    meta: {
      requiresAuth: true,
      text: "State",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Operating Zones", to: "/operatingZones/overview" },
        { text: `State` },
      ],
    },
  },
  {
    path: "/territory/new",
    name: "territory-new",
    component: () =>
      import(
        "@/views/phases/0_admin/operatingZones/territories/Territory_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Territory",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Operating Zones", to: "/operatingZones/overview" },
        { text: "New Territory" },
      ],
    },
  },
  {
    path: "/territory/:id",
    name: "territory-view",
    component: () =>
      import(
        "@/views/phases/0_admin/operatingZones/territories/Territory_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Territory",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Operating Zones", to: "/operatingZones/overview" },
        { text: `Territory` },
      ],
    },
  },
  // Email Templates
  {
    path: "/emailTemplates/overview",
    name: "emailTemplates-overview",
    component: () =>
      import(
        "@/views/phases/0_admin/emailTemplates/EmailTemplates_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Email Templates",
      trail: () => [{ text: "Home", to: "/" }, { text: "Email Templates" }],
    },
  },
  {
    path: "/emailTemplate/new",
    name: "emailTemplate-new",
    component: () =>
      import("@/views/phases/0_admin/emailTemplates/EmailTemplate_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Email Template",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Email Templates", to: "/emailTemplates/overview" },
        { text: "New Template" },
      ],
    },
  },
  {
    path: "/emailTemplate/:id",
    name: "emailTemplate-view",
    component: () =>
      import("@/views/phases/0_admin/emailTemplates/EmailTemplate_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Email Template",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Email Templates", to: "/emailTemplates/overview" },
        { text: `Template` },
      ],
    },
  },
  // Email
  {
    path: "/emails/overview",
    name: "emails",
    component: () => import("@/views/phases/0_admin/emails/Email_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Emails",
      trail: () => [{ text: "Home", to: "/" }, { text: "Emails" }],
    },
  },
  // Tasks
  {
    path: "/tasks/overview",
    name: "tasks",
    component: () => import("@/views/phases/0_admin/tasks/Tasks_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Tasks",
      trail: () => [{ text: "Home", to: "/" }, { text: "Tasks" }],
    },
  },
  {
    path: "/task/new",
    name: "task-new",
    component: () => import("@/views/phases/0_admin/tasks/Task_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Task",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Tasks", to: "/tasks/overview" },
        { text: "New Task" },
      ],
    },
  },
  {
    path: "/task/:id",
    name: "task-view",
    component: () => import("@/views/phases/0_admin/tasks/Task_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Task",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Tasks", to: "/tasks/overview" },
        { text: `Task` },
      ],
    },
  },
  // Project Templates
  {
    path: "/projectTemplates/overview",
    name: "projectTemplates",
    component: () =>
      import(
        "@/views/phases/0_admin/projectTemplates/ProjectTemplates_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Project Templates",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Project Templates: Overview" },
      ],
    },
  },
  {
    path: "/projectTemplates/new",
    name: "projectTemplates-new",
    component: () =>
      import(
        "@/views/phases/0_admin/projectTemplates/ProjectTemplates_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Project Template",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Project Templates", to: "/projectTemplates/overview" },
        { text: "New Template" },
      ],
    },
  },
  {
    path: "/projectTemplates/:id",
    name: "projectTemplates-view",
    component: () =>
      import(
        "@/views/phases/0_admin/projectTemplates/ProjectTemplates_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Project Template",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Project Templates", to: "/projectTemplates/overview" },
        { text: `Template` },
      ],
    },
  },
  // Menu
  {
    path: "/menus/overview",
    name: "menus-overview",
    component: () => import("@/views/phases/0_admin/menus/Menus_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Menus",
      trail: () => [{ text: "Home", to: "/" }, { text: "Menus: Overview" }],
    },
  },
  {
    path: "/menu/new",
    name: "menus-new",
    component: () => import("@/views/phases/0_admin/menus/Menus_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Menu",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Menus", to: "/menus/overview" },
        { text: "New Menu" },
      ],
    },
  },
  {
    path: "/menu/:id",
    name: "menu-view",
    component: () => import("@/views/phases/0_admin/menus/Menu_View.vue"),
    meta: {
      requiresAuth: false,
      text: "View Menu",
      trail: () => [
        // { text: "Home", to: "/" },
        // { text: "Menus", to: "/menus/overview" },
        // { text: "View Menu" },
      ],
    },
  },
  // {
  //   path: "/projectTemplates/:id",
  //   name: "projectTemplates-view",
  //   component: () =>
  //     import(
  //       "@/views/phases/0_admin/projectTemplates/ProjectTemplates_View.vue"
  //     ),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Project Template",
  //     trail: (route) => [
  //       { text: "Home", to: "/" },
  //       { text: "Project Templates", to: "/projectTemplates/overview" },
  //       { text: `Template` },
  //     ],
  //   },
  // },
  // Project Management
  {
    path: "/projects/overview",
    name: "projects-overview",
    component: () =>
      import("@/views/phases/0_admin/projects/Projects_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Projects",
      trail: () => [{ text: "Home", to: "/" }, { text: "Projects: Overview" }],
    },
  },
  {
    path: "/project/new",
    name: "projects-new",
    component: () => import("@/views/phases/0_admin/projects/Project_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Project",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Projects", to: "/projects/overview" },
        { text: "New Project" },
      ],
    },
  },
  {
    path: "/project/:id",
    name: "projects-view",
    component: () => import("@/views/phases/0_admin/projects/Project_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Project",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Projects", to: "/projects/overview" },
        { text: `Project` },
      ],
    },
  },
  // QR Codes
  {
    path: "/qrCodes/overview",
    name: "qrCodes",
    component: () =>
      import("@/views/phases/0_admin/qrCodes/qrCodes_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "QR Codes",
      trail: () => [{ text: "Home", to: "/" }, { text: "QR Codes: Overview" }],
    },
  },
  {
    path: "/qrCodes/new",
    name: "qrCodes-new",
    component: () => import("@/views/phases/0_admin/qrCodes/qrCodes_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New QR Code",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "QR Codes", to: "/qrCodes/overview" },
        { text: "New QR Code" },
      ],
    },
  },
  {
    path: "/qrCodes/:id",
    name: "qrCodes-view",
    component: () => import("@/views/phases/0_admin/qrCodes/qrCodes_View.vue"),
    meta: {
      requiresAuth: true,
      text: "QR Code",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "QR Codes", to: "/qrCodes/overview" },
        { text: `QR Code` },
      ],
    },
  },
  {
    path: "/qrcode/:id",
    name: "qrCodes-read",
    component: () => import("@/views/phases/0_admin/qrCodes/qrCode_Read.vue"),
    meta: {
      requiresAuth: false,
      text: "Read QR Code",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "QR Codes", to: "/qrCodes/overview" },
        { text: `Read QR Code` },
      ],
    },
  },
  // Contacts
  {
    path: "/contact/new",
    name: "contacts-new",
    component: () => import("@/components/misc/contacts/Contact_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Contact",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Contacts", to: "/contacts/overview" },
        { text: "New Contact" },
      ],
    },
  },
  {
    path: "/contact/:id",
    name: "contacts-view",
    component: () => import("@/components/misc/contacts/Contact_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Contact",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Contacts", to: "/contacts/overview" },
        { text: `Contact` },
      ],
    },
  },
  /* Phase 1 - Sourcing */
  // Vendors
  {
    path: "/vendors/overview",
    name: "vendors-overview",
    component: () =>
      import("@/views/phases/1_sourcing/vendors/Vendors_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Vendors",
      trail: () => [{ text: "Home", to: "/" }, { text: "Vendors: Overview" }],
    },
  },
  {
    path: "/vendor/new",
    name: "vendors-new",
    component: () => import("@/views/phases/1_sourcing/vendors/Vendor_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Vendor",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Vendors", to: "/vendors/overview" },
        { text: "New Vendor" },
      ],
    },
  },
  {
    path: "/vendor/:id",
    name: "vendors-view",
    component: () =>
      import("@/views/phases/1_sourcing/vendors/Vendor_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Vendor",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Vendors", to: "/vendors/overview" },
        { text: `Vendor` },
      ],
    },
  },
  // Brands
  {
    path: "/brands/overview",
    name: "brands-overview",
    component: () =>
      import("@/views/phases/1_sourcing/brands/Brands_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Brands",
      trail: () => [{ text: "Home", to: "/" }, { text: "Brands: Overview" }],
    },
  },
  {
    path: "/brand/new",
    name: "brand-new",
    component: () =>
      import("@/views/phases/1_sourcing/brands/create/brand/Brand_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Brand",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Brands", to: "/brands/overview" },
        { text: "New Brand" },
      ],
    },
  },
  {
    path: "/brand/:id",
    name: "brand-view",
    component: () =>
      import("@/views/phases/1_sourcing/brands/update/brand/Brand_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Brand",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Brands", to: "/brands/overview" },
        { text: `Brand` },
      ],
    },
  },
  {
    path: "/brand/:id/licensee/new",
    name: "payableTo-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/brands/create/payableTo/PayableTo_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Licensee",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Brands", to: "/brands/overview" },
        { text: "New Licensee" },
      ],
    },
  },
  {
    path: "/licensee/:id",
    name: "payableTo-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/brands/update/payableTo/PayableTo_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Licensee",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Brands", to: "/brands/overview" },
        { text: `Brand - Licensee` },
      ],
    },
  },
  {
    path: "/priceOverride/:id",
    name: "priceOverride-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/brands/update/priceOverride/PriceOverride_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Price Override",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Price Overrides", to: "/priceOverrides/overview" },
        { text: `Price Override` },
      ],
    },
  },
  // Product
  {
    path: "/products/overview",
    name: "products-overview",
    component: () =>
      import("@/views/phases/1_sourcing/products/Products_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Products",
      trail: () => [{ text: "Home", to: "/" }, { text: "Products: Overview" }],
    },
  },
  {
    path: "/category/new",
    name: "category-new",
    component: () =>
      import("@/views/phases/1_sourcing/products/create/Category_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Category",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Product Categories", to: "/products/overview" },
        { text: "New Category" },
      ],
    },
  },
  {
    path: "/category/:id",
    name: "category-view",
    component: () =>
      import("@/views/phases/1_sourcing/products/update/Category_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Category",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Product Categories", to: "/products/overview" },
        { text: `Category` },
      ],
    },
  },
  {
    path: "/product/new",
    name: "products-new",
    component: () =>
      import("@/views/phases/1_sourcing/products/create/Product_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Product",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Products", to: "/products/overview" },
        { text: "New Product" },
      ],
    },
  },
  {
    path: "/product/:id",
    name: "products-view",
    component: () =>
      import("@/views/phases/1_sourcing/products/update/Product_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Product",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Products", to: "/products/overview" },
        { text: `Product` },
      ],
    },
  },
  // Locations
  {
    path: "/locations/overview",
    name: "locations-overview",
    component: () =>
      import("@/views/phases/1_sourcing/locations/Locations_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Locations: Overview",
      trail: () => [{ text: "Home", to: "/" }, { text: "Locations: Overview" }],
    },
  },
  {
    path: "/location/new",
    name: "locations-new",
    component: () =>
      import("@/views/phases/1_sourcing/locations/Location_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Location",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        { text: "New Location" },
      ],
    },
  },
  {
    path: "/location/:id",
    name: "locations-view",
    component: () => import("@/views/phases/1_sourcing/locations/Location.vue"),
    meta: {
      requiresAuth: true,
      text: "Location",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        { text: `${route.params.name || "Location"}` },
      ],
    },
  },
  // Location Raw Materials
  {
    path: "/location/:locationID/rawMaterials/overview",
    name: "location-rawMaterials-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/rawMaterials/RawMaterials_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Location Raw Materials",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        { text: "Raw Materials" },
      ],
    },
  },
  // Location Product Batches
  {
    path: "/location/:locationID/productLots/overview",
    name: "location-productLots-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/productLots/LocationProductLots_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Location Product Batches",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        { text: "Product Batches" },
      ],
    },
  },
  // Location Component Batches
  {
    path: "/location/:locationID/componentBatches/overview",
    name: "location-componentBatches-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/componentBatches/LocationComponentBatches_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: "/componentBatch/:id",
    name: "componentBatch-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/componentBatches/ComponentBatch_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Location Product Batches",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        { text: "Product Batches" },
      ],
    },
  },
  // Location Cultivation
  {
    path: "/location/:locationID/cultivation/overview",
    name: "location-cultivation-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/cultivation/Cultivation_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Location Cultivation",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        {
          text: `Location ${route.params.locationID}`,
          to: `/location/${route.params.locationID}`,
        },
        { text: "Cultivation" },
      ],
    },
  },
  {
    path: "/location/:locationID/room/add",
    name: "cultivation-room-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/cultivation/rooms/Room_Add.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Cultivation Room",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        {
          text: `Location ${route.params.locationID}`,
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Cultivation",
          to: `/location/${route.params.locationID}/cultivation/overview`,
        },
        { text: "New Room" },
      ],
    },
  },
  {
    path: "/room/:id",
    name: "cultivation-room-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/cultivation/rooms/Room_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Cultivation Room",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        { text: "Cultivation", to: "/cultivation/overview" },
        { text: `Room` },
      ],
    },
  },
  // Harvest
  {
    path: "/location/:locationID/harvest/add",
    name: "harvest-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/cultivation/harvest/Harvest_Add.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Harvest",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        {
          text: `Location ${route.params.locationID}`,
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Cultivation",
          to: `/location/${route.params.locationID}/cultivation/overview`,
        },
        { text: "New Harvest" },
      ],
    },
  },
  {
    path: "/harvest/:id",
    name: "harvests-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/cultivation/harvest/Harvest_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Harvest",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        { text: "Cultivation", to: "/cultivation/overview" },
        { text: `Harvest` },
      ],
    },
  },
  // Reconcile Inventory
  {
    path: "/location/:locationID/reconcile/overview",
    name: "inventory-reconcile-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/reconcile/Reconcile_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Reconcile Inventory",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        { text: "Reconcile Inventory" },
      ],
    },
  },
  // Transfer Inventory- Overview
  {
    path: "/location/:locationID/transfer/overview",
    name: "transfer-inventory-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/transfer/TransferInventory_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Transfer Overview",
      trail: (route) => {
        const locationID = route.params?.locationID || "…";

        return [
          { text: "Home", to: "/" },
          { text: "Locations", to: "/locations/overview" },
          {
            text: `${route.params.name || route.query.name || "Location"}`,
            to: `/location/${locationID}`,
            disabled: !locationID,
          },
          { text: "Transfer Overview" },
        ];
      },
    },
  },
  // Transfer Inventory - Select New Location
  {
    path: "/location/:locationID/transfer/location",
    name: "new-transfer-location-select",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/transfer/create/TransferInventory_Location.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Select Transfer Location",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        {
          text: `${route.params.name || route.query.name || "Location"}`,
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Transfer Overview",
          to: `/location/${route.params.locationID}/transfer/overview`,
        },
        { text: "Select Location" },
      ],
    },
  },
  // Transfer Inventory - Assembled New
  {
    path: "/location/:locationID/transfer/assembled",
    name: "transfer-assembled-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/transfer/create/TransferAssembled_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Assembled Transfer",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        {
          text: `${route.params.name || route.query.name || "Location"}`,
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Transfer Overview",
          to: `/location/${route.params.locationID}/transfer/overview`,
        },
        { text: "New Assembled Transfer" },
      ],
    },
  },
  // Transfer Inventory - Assembled View
  {
    path: "/transfer/:locationID/assembled/:id",
    name: "transfer-assembled-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/transfer/update/TransferAssembled_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Assembled Transfer",
      trail: (route) => {
        const locationID = route.params?.locationID || "";

        return [
          { text: "Home", to: "/" },
          { text: "Locations", to: "/locations/overview" },
          {
            text: `${route.params.name || route.query.name || "Location"}`,
            to: `/location/${locationID}`,
            disabled: !locationID,
          },
          {
            text: "Transfer Overview",
            to: `/location/${locationID}/transfer/overview`,
            disabled: !locationID,
          },
          { text: "Assembled Transfer" },
        ];
      },
    },
  },
  // Transfer Inventory - Components New
  {
    path: "/location/:locationID/transfer/components",
    name: "transfer-components-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/transfer/create/TransferComponents_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Components Transfer",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        {
          text: `${route.params.name || route.query.name || "Location"}`,
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Transfer Overview",
          to: `/location/${route.params.locationID}/transfer/overview`,
        },
        { text: "New Components Transfer" },
      ],
    },
  },
  // Transfer Inventory - Components View
  {
    path: "/transfer/:locationID/components/:id",
    name: "transfer-components-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/transfer/update/TransferComponents_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Components Transfer",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        {
          text: `${route.params.name || route.query.name || "Location"}`,
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Transfer Overview",
          to: `/location/${route.params.locationID}/transfer/overview`,
        },
        { text: `Components Transfer` },
      ],
    },
  },
  {
    path: "/location/:locationID/transfer/rawMaterials",
    name: "transfer-rawMaterials-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/transfer/create/TransferRawMaterials_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Raw Materials Transfer",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        {
          text: `Location ${route.params.locationID}`,
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Transfer Inventory",
          to: `/location/${route.params.locationID}/transfer/overview`,
        },
        { text: "New Raw Materials Transfer" },
      ],
    },
  },
  {
    path: "/transfer/:locationID/rawMaterials/:id",
    name: "transfer-rawMaterials-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/transfer/update/TransferRawMaterials_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Raw Materials Transfer",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Locations", to: "/locations/overview" },
        {
          text: `Location ${route.params.locationID}`,
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Transfer Inventory",
          to: `/location/${route.params.locationID}/transfer/overview`,
        },
        { text: `Transfer` },
      ],
    },
  },
  // Component batches
  {
    path: "/componentBatches/overview",
    name: "componentBatches-overview",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/componentBatches/ComponentBatches_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Component Batches",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Component Batches: Overview", to: "/componentBatches" },
      ],
    },
  },
  // Work Orders
  {
    path: "/workOrders/overview",
    name: "workOrders-overview",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/workOrders/WorkOrders_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Work Orders",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Work Orders: Overview", to: "/workOrders" },
      ],
    },
  },
  {
    path: "/location/:locationID/workOrders/overview",
    name: "manufacturing-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/manufacturing/ManufacturingOrders_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Work Orders",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        { text: "Work Orders" },
      ],
    },
  },
  {
    path: "/location/:locationID/workOrder/new/brand",
    name: "workOrder-brand-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/manufacturing/create/WorkOrder_Brand.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Select Work Order Brand",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Work Orders",
          to: `/location/${route.params.locationID}/workOrders/overview`,
        },
        { text: "New Work Order" },
      ],
    },
  },
  {
    path: "/location/:locationID/workOrder/new/products",
    name: "new-workOrder-products",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/manufacturing/create/WorkOrder_Products.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Select Work Order Products",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Work Orders",
          to: `/location/${route.params.locationID}/workOrders/overview`,
        },
        { text: "New Work Order" },
      ],
    },
  },
  {
    path: "/location/:locationID/workOrder/new/review",
    name: "new-workOrder-review",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/manufacturing/create/WorkOrder_Review.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Review Work Order",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Work Orders",
          to: `/location/${route.params.locationID}/workOrders/overview`,
        },
        { text: "New Work Order" },
      ],
    },
  },
  {
    path: "/mainWorkOrder/:id",
    name: "mainWorkOrder-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/manufacturing/update/MainWorkOrder_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Main Work Order",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Work Orders", to: "/workOrders/overview" },
        { text: `Work Order` },
      ],
    },
  },
  {
    path: "/productOrder/:id",
    name: "productWorkOrder-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/manufacturing/update/ProductWorkOrder_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Product Work Order",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Work Orders", to: "/workOrders/overview" },
        { text: `Product Work Order` },
      ],
    },
  },
  // White Label Orders Overview
  {
    path: "/location/:locationID/whiteLabelOrders/overview",
    name: "location-white-label-orders-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/whiteLabelOrders/WhiteLabelOrders_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "White Label Orders",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        { text: "White Label Orders Overview" },
      ],
    },
  },
  // White Label Invoice Create
  {
    path: "/location/:locationID/whiteLabelInvoice/new/select",
    name: "whiteLabelOrder-new-select",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/whiteLabelOrders/create/WhiteLabelOrder_Select.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Select White Label Order",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "Orders Overview",
          to: `/location/${route.params.locationID}/whiteLabelOrders/overview`,
        },
        { text: "New White Label Order" },
      ],
    },
  },
  {
    path: "/location/:locationID/whiteLabelInvoice/new/review",
    name: "whiteLabelOrder-new-review",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/whiteLabelOrders/create/WhiteLabelOrder_Review.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Review White Label Order",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Overview", to: "/locations/overview" },
        {
          text: "Location",
          to: `/location/${route.params.locationID}`,
        },
        {
          text: "White Label Orders",
          to: `/location/${route.params.locationID}/whiteLabelOrders/overview`,
        },
        { text: "New White Label Order" },
      ],
    },
  },
  // White Label Invoice View
  {
    path: "/location/:locationID/whiteLabelInvoice/:id",
    name: "whiteLabelInvoice-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/locations/whiteLabelOrders/update/WhiteLabelOrder_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "White Label Order",
      trail: (route) => {
        const locationID = route.params?.locationID || "";
        return [
          { text: "Home", to: "/" },
          { text: "Overview", to: "/locations/overview" },
          {
            text: "Location",
            to: `/location/${locationID}`,
          },
          {
            text: "Orders Overview",
            to: `/location/${locationID}/whiteLabelOrders/overview`,
          },
          { text: "White Label Order" },
        ];
      },
    },
  },
  // Component Orders
  {
    path: "/vendorOrders/overview",
    name: "vendorOrders-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/vendorOrders/VendorOrders_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Component Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Component Orders: Overview" },
      ],
    },
  },
  {
    path: "/vendorOrder/new/brand",
    name: "vendorOrder-brand-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/vendorOrders/create/VendorOrder_Brand.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Component Order - Brand",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Vendor Orders", to: "/vendorOrders/overview" },
        { text: "New Component Order - Brand" },
      ],
    },
  },
  {
    path: "/vendorOrder/new/products",
    name: "vendorOrder-products-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/vendorOrders/create/VendorOrder_Products.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Component Order - Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Component Orders", to: "/vendorOrders/overview" },
        { text: "New Component Order - Products" },
      ],
    },
  },
  {
    path: "/vendorOrder/new/review",
    name: "vendorOrder-review-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/vendorOrders/create/VendorOrder_Review.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Vendor Order - Review",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Vendor Orders", to: "/vendorOrders/overview" },
        { text: "New Component Order - Review" },
      ],
    },
  },
  {
    path: "/vendorOrder/master/:id",
    name: "vendorOrder-master-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/vendorOrders/update/VendorOrder_Master.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Vendor Order Master",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Vendor Orders", to: "/vendorOrders/overview" },
        { text: `Order Master` },
      ],
    },
  },
  {
    path: "/vendorOrder/category/:id",
    name: "vendorOrder-productCategory-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/vendorOrders/update/VendorOrder_ProductCategory.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Component Order Category",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Component Orders", to: "/vendorOrders/overview" },
        { text: `Order Category` },
      ],
    },
  },
  {
    path: "/vendorOrder/invoice/:id",
    name: "vendorOrder-invoice-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/vendorOrders/update/VendorOrder_Invoice.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Vendor Component Invoice",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Component Orders", to: "/vendorOrders/overview" },
        { text: `Component Invoice` },
      ],
    },
  },
  // Custom Products
  {
    path: "/customProducts/overview",
    name: "customProducts-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/customProducts/CustomProducts_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Custom Products",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Custom Products: Overview" },
      ],
    },
  },
  {
    path: "/customProductCategory/new",
    name: "customProductCategory-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/customProducts/category/CustomProductCategory_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Custom Product Category",
      trail: () => [
        { text: "Home", to: "/" },
        {
          text: "Custom Product Categories",
          to: "/customProducts/overview",
        },
        { text: "New Category" },
      ],
    },
  },
  {
    path: "/customProductCategory/:id",
    name: "customProductCategory-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/customProducts/category/CustomProductCategory_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Custom Product Category",
      trail: (route) => [
        { text: "Home", to: "/" },
        {
          text: "Custom Products",
          to: "/customProducts/overview",
        },
        { text: `Category` },
      ],
    },
  },
  {
    path: "/customProducts/new",
    name: "customProducts-new",
    component: () =>
      import(
        "@/views/phases/1_sourcing/customProducts/products/CustomProduct_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Custom Product",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Custom Products", to: "/customProducts/overview" },
        { text: "New Product" },
      ],
    },
  },
  {
    path: "/customProduct/:id",
    name: "customProduct-view",
    component: () =>
      import(
        "@/views/phases/1_sourcing/customProducts/products/CustomProduct_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Custom Product",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Custom Products", to: "/customProducts/overview" },
        { text: `Product` },
      ],
    },
  },
  // Custom Orders
  {
    path: "/customOrders/overview",
    name: "customOrders-overview",
    component: () =>
      import(
        "@/views/phases/1_sourcing/customOrders/CustomOrders_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Custom Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Custom Orders: Overview" },
      ],
    },
  },
  {
    path: "/customOrder/new",
    name: "customOrder-new",
    component: () =>
      import("@/views/phases/1_sourcing/customOrders/CustomOrder_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Custom Order",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Custom Orders", to: "/customOrders/overview" },
        { text: "New Order" },
      ],
    },
  },
  {
    path: "/customOrder/:id",
    name: "customOrder-view",
    component: () =>
      import("@/views/phases/1_sourcing/customOrders/CustomOrder_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Custom Order",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Custom Orders", to: "/customOrders/overview" },
        { text: `Order` },
      ],
    },
  },
  /* Phase 2 - Manufacturing */
  // Testing Labs
  {
    path: "/testingLabs/overview",
    name: "testingLabs-overview",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/testingLabs/TestingLabs_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Testing Labs",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Testing Labs: Overview" },
      ],
    },
  },
  {
    path: "/testingLab/new",
    name: "testingLabs-new",
    component: () =>
      import("@/views/phases/2_manufacturing/testingLabs/TestingLab_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Testing Lab",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Testing Labs", to: "/testingLabs/overview" },
        { text: "New Lab" },
      ],
    },
  },
  {
    path: "/testingLab/:id",
    name: "testingLabs-view",
    component: () =>
      import("@/views/phases/2_manufacturing/testingLabs/TestingLab_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Testing Lab",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Testing Labs", to: "/testingLabs/overview" },
        { text: `Lab` },
      ],
    },
  },
  // Terpenes
  // {
  //   path: "/terpenes/overview",
  //   name: "terpenes-overview",
  //   component: () =>
  //     import("@/views/phases/2_manufacturing/terpenes/Terpenes_Overview.vue"),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Terpenes",
  //     trail: () => [{ text: "Home", to: "/" }, { text: "Terpenes" }],
  //   },
  // },
  // {
  //   path: "/terpene/new",
  //   name: "terpenes-new",
  //   component: () =>
  //     import("@/views/phases/2_manufacturing/terpenes/Terpene_New.vue"),
  //   meta: {
  //     requiresAuth: true,
  //     text: "New Terpene",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Terpenes", to: "/terpenes/overview" },
  //       { text: "New Terpene" },
  //     ],
  //   },
  // },
  // {
  //   path: "/terpene/:id",
  //   name: "terpenes-view",
  //   component: () =>
  //     import("@/views/phases/2_manufacturing/terpenes/Terpene_View.vue"),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Terpene",
  //     trail: (route) => [
  //       { text: "Home", to: "/" },
  //       { text: "Terpenes", to: "/terpenes/overview" },
  //       { text: `Terpene` },
  //     ],
  //   },
  // },
  // Raw Materials
  {
    path: "/rawMaterials/overview",
    name: "rawMaterials-overview",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterials/RawMaterials_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Raw Materials",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Raw Materials: Overview" },
      ],
    },
  },

  {
    path: "/rawMaterialCategory/new",
    name: "rawMaterialCategory-new",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterials/category/RawMaterialCategory_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Raw Material Category",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Raw Materials", to: "/rawMaterials/overview" },
        { text: "New Category" },
      ],
    },
  },
  {
    path: "/rawMaterialCategory/:id",
    name: "rawMaterialCategory-view",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterials/category/RawMaterialCategory_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Raw Material Category",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Raw Materials", to: "/rawMaterials/overview" },
        { text: `Category` },
      ],
    },
  },
  {
    path: "/rawMaterial/new",
    name: "rawMaterials-new",
    component: () =>
      import("@/views/phases/2_manufacturing/rawMaterials/RawMaterial_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Raw Material",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Raw Materials", to: "/rawMaterials/overview" },
        { text: "New Material" },
      ],
    },
  },
  {
    path: "/rawMaterial/:id",
    name: "rawMaterials-view",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterials/RawMaterial_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Raw Material",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Raw Materials", to: "/rawMaterials/overview" },
        { text: `Material` },
      ],
    },
  },
  // Product Recipes
  {
    path: "/productRecipes/overview",
    name: "productRecipes-overview",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/productRecipes/ProductRecipes_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Product Recipes: Overview",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Product Recipes: Overview" },
      ],
    },
  },
  {
    path: "/productRecipes/edit",
    name: "productRecipes-edit",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/productRecipes/ProductRecipes_Edit.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Edit Product Recipes",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Product Recipes", to: "/productRecipes/overview" },
        { text: "Edit Recipes" },
      ],
    },
  },
  // Raw Material Vendors
  {
    path: "/rawMaterialVendors/overview",
    name: "rawMaterialVendors-overview",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterialVendors/RawMaterialVendors_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Raw Material Vendors",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Raw Material Vendors: Overview" },
      ],
    },
  },
  {
    path: "/rawMaterialVendor/new",
    name: "rawMaterialVendors-new",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterialVendors/RawMaterialVendor_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Raw Material Vendor",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Raw Material Vendors", to: "/rawMaterialVendors/overview" },
        { text: "New Vendor" },
      ],
    },
  },
  {
    path: "/rawMaterialVendor/:id",
    name: "rawMaterialVendors-view",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterialVendors/RawMaterialVendor_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Raw Material Vendor",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Raw Material Vendors", to: "/rawMaterialVendors/overview" },
        { text: `Vendor` },
      ],
    },
  },
  // Raw Material Orders
  {
    path: "/rawMaterialOrders/overview",
    name: "rawMaterialOrders-overview",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterialOrders/RawMaterialOrders_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Raw Material Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Raw Material Orders: Overview" },
      ],
    },
  },
  {
    path: "/rawMaterialOrder/new",
    name: "rawMaterialOrder-new",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterialOrders/RawMaterialOrder_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Raw Material Order",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Raw Material Orders", to: "/rawMaterialOrders/overview" },
        { text: "New Order" },
      ],
    },
  },
  {
    path: "/rawMaterialOrders/:id",
    name: "rawMaterialOrders-view",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/rawMaterialOrders/RawMaterialOrder_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Raw Material Order",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Raw Material Orders", to: "/rawMaterialOrders/overview" },
        { text: `Order` },
      ],
    },
  },
  // Product Batches
  {
    path: "/productLots/overview",
    name: "productLots-overview",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/productLots/ProductLots_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Product Batches",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Product Batches: Overview" },
      ],
    },
  },
  {
    path: "/productLot/new",
    name: "productLot-new",
    component: () =>
      import("@/views/phases/2_manufacturing/productLots/ProductLot_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Product Batches",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Product Batches", to: "/productLots/overview" },
        { text: "New Batch" },
      ],
    },
  },
  {
    path: "/productLot/:id",
    name: "productLot-view",
    component: () =>
      import("@/views/phases/2_manufacturing/productLots/ProductLot_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Product Lot",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Product Batches", to: "/productLots/overview" },
        { text: `Batch` },
      ],
    },
  },
  // White Label Clients
  {
    path: "/whiteLabelClients/overview",
    name: "whiteLabelClients-overview",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/whiteLabelClients/WhiteLabelClients_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "White Label Clients",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "White Label Clients: Overview" },
      ],
    },
  },
  {
    path: "/whiteLabelClient/new",
    name: "whiteLabelClients-new",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/whiteLabelClients/WhiteLabelClient_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New White Label Client",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "White Label Clients", to: "/whiteLabelClients/overview" },
        { text: "New Client" },
      ],
    },
  },
  {
    path: "/whiteLabelClient/:id",
    name: "whiteLabelClients-view",
    component: () =>
      import(
        "@/views/phases/2_manufacturing/whiteLabelClients/WhiteLabelClient_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "White Label Client",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "White Label Clients", to: "/whiteLabelClients/overview" },
        { text: `Client` },
      ],
    },
  },
  /* Phase 3 - Sales */
  // Sales Contacts
  {
    path: "/sales/contacts/overview",
    name: "salesContacts-overview",
    component: () =>
      import("@/views/phases/3_sales/contacts/SalesContacts_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Contacts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Sales", to: "/sales" },
        { text: "Contacts" },
      ],
    },
  },
  // Parent Accounts
  {
    path: "/parentAccounts/overview",
    name: "parentAccounts-overview",
    component: () =>
      import(
        "@/views/phases/3_sales/parentAccounts/ParentAccounts_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Parent Accounts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Parent Accounts: Overview" },
      ],
    },
  },
  {
    path: "/parentAccount/new",
    name: "parentAccounts-new",
    component: () =>
      import("@/views/phases/3_sales/parentAccounts/ParentAccount_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Parent Account",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Parent Accounts", to: "/parentAccounts/overview" },
        { text: "New Parent Account" },
      ],
    },
  },
  {
    path: "/parentAccount/:id",
    name: "parentAccounts-view",
    component: () =>
      import("@/views/phases/3_sales/parentAccounts/ParentAccount_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Parent Account",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Parent Accounts", to: "/parentAccounts/overview" },
        { text: `Account` },
      ],
    },
  },
  // Accounts
  {
    path: "/accounts/overview",
    name: "accounts-overview",
    component: () =>
      import("@/views/phases/3_sales/accounts/Accounts_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Accounts",
      trail: () => [{ text: "Home", to: "/" }, { text: "Accounts: Overview" }],
    },
  },
  {
    path: "/account/new",
    name: "accounts-new",
    component: () => import("@/views/phases/3_sales/accounts/Account_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Account",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Accounts", to: "/accounts/overview" },
        { text: "New Account" },
      ],
    },
  },
  {
    path: "/account/:id",
    name: "accounts-view",
    component: () => import("@/views/phases/3_sales/accounts/Account_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Account",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Accounts", to: "/accounts/overview" },
        { text: `Account` },
      ],
    },
  },
  // Account Onboarding
  {
    path: "/accountOnboarding/overview",
    name: "accountOnboardings-overview",
    component: () =>
      import(
        "@/views/phases/3_sales/accountOnboarding/AccountOnboarding_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Account Onboarding",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Account Onboarding: Overview" },
      ],
    },
  },
  {
    path: "/accountOnboarding/new",
    name: "accountOnboardings-new",
    component: () =>
      import(
        "@/views/phases/3_sales/accountOnboarding/AccountOnboarding_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Account Onboarding",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Account Onboarding", to: "/accountOnboarding/overview" },
        { text: "New Onboarding" },
      ],
    },
  },
  {
    path: "/accountOnboarding/:id",
    name: "accountOnboardings-view",
    component: () =>
      import(
        "@/views/phases/3_sales/accountOnboarding/AccountOnboarding_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Account Onboarding",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Account Onboarding", to: "/accountOnboarding/overview" },
        { text: `Onboarding` },
      ],
    },
  },
  // Sales Orders
  {
    path: "/draftOrders/overview",
    name: "draftOrders-overview",
    component: () =>
      import("@/views/phases/3_sales/salesOrders/DraftOrders_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Draft Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Sales Orders: Drafts" },
      ],
    },
  },
  {
    path: "/salesOrders/overview",
    name: "saleOrders-overview",
    component: () =>
      import("@/views/phases/3_sales/salesOrders/SalesOrders_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Sales Orders: In-Progress" },
      ],
    },
  },
  {
    path: "/salesOrder/:brandStatsID/new",
    name: "salesOrder-new",
    component: () =>
      import("@/views/phases/3_sales/salesOrders/SalesOrder_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Sales Order",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Sales Orders", to: "/salesOrders/overview" },
        { text: "New Order" },
      ],
    },
  },
  {
    path: "/salesOrder/:id",
    name: "salesOrder-view",
    component: () =>
      import("@/views/phases/3_sales/salesOrders/SalesOrder_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Order",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Sales Orders", to: "/salesOrders/overview" },
        { text: `Order` },
      ],
    },
  },
  {
    path: "/salesOrderPublic/:id",
    name: "salesOrder-customer-view",
    component: () =>
      import("@/views/phases/3_sales/salesOrders/SalesOrder_CustomerView.vue"),
    meta: {
      requiresAuth: false,
      text: "Sales Order",
      trail: (route) => [{ text: "Order" }],
    },
  },
  {
    path: "/salesOrderCustomer/:id",
    redirect: (to) => ({ path: `/salesOrderPublic/${to.params.id}` }),
  },
  {
    path: "/order/:id",
    redirect: (to) => ({ path: `/salesOrderPublic/${to.params.id}` }),
  },
  // Delivery Companies
  {
    path: "/deliveryLogistics/overview",
    name: "deliveryLogistics-overview",
    component: () =>
      import(
        "@/views/phases/3_sales/deliveryLogistics/DeliveryLogistics_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Delivery Companies",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Delivery Companies: Overview" },
      ],
    },
  },
  {
    path: "/deliveryLogistic/new",
    name: "deliveryLogistic-new",
    component: () =>
      import(
        "@/views/phases/3_sales/deliveryLogistics/DeliveryLogistic_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Delivery Company",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Delivery Companies", to: "/deliveryLogistics/overview" },
        { text: "New Company" },
      ],
    },
  },
  {
    path: "/deliveryLogistic/:id",
    name: "deliveryLogistic-view",
    component: () =>
      import(
        "@/views/phases/3_sales/deliveryLogistics/DeliveryLogistic_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Delivery Company",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Delivery Companies", to: "/deliveryLogistics/overview" },
        { text: `Company` },
      ],
    },
  },
  {
    path: "/deliveredOrders/overview",
    name: "deliveredOrders-overview",
    component: () =>
      import("@/views/phases/3_sales/salesOrders/DeliveredOrders_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Open Balances",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Sales Orders: Open Balances" },
      ],
    },
  },
  // Licensee Sales
  {
    path: "/licenseeSales/overview",
    name: "licenseeSales-overview",
    component: () =>
      import("@/views/phases/3_sales/licenseeSales/LicenseeSales_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Licensee Sales",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Licensee Sales: Overview" },
      ],
    },
  },
  {
    path: "/licenseeSales/new",
    name: "licenseeSales-new",
    component: () =>
      import("@/views/phases/3_sales/licenseeSales/LicenseeSales_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Licensee Sale",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Licensee Sales", to: "/licenseeSales/overview" },
        { text: "New Sale" },
      ],
    },
  },
  // Payments
  {
    path: "/payments/overview",
    name: "payments-overview",
    component: () =>
      import("@/views/phases/3_sales/payments/Payments_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Payments",
      trail: () => [{ text: "Home", to: "/" }, { text: "Payments: Overview" }],
    },
  },
  // Menus
  {
    path: "/specials/overview",
    name: "specials-overview",
    component: () =>
      import("@/views/phases/3_sales/specials/Specials_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Menus",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Menus: Overview", to: "/specials/overview" },
      ],
    },
  },
  {
    path: "/specials/new",
    name: "specials-new",
    component: () => import("@/views/phases/3_sales/specials/Special_New.vue"),
    meta: {
      requiresAuth: true,
      text: "Menu New",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Menus", to: "/specials/overview" },
        { text: `New Menu` },
      ],
    },
  },
  {
    path: "/specials/:id",
    name: "specials-view",
    component: () => import("@/views/phases/3_sales/specials/Special_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Menu",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Menus", to: "/specials/overview" },
        { text: `Menu` },
      ],
    },
  },
  {
    path: "/special/:id",
    name: "special-read",
    component: () => import("@/views/phases/3_sales/specials/Special_Read.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  /* Phase 4 - Retail Success */
  // Support Contacts
  {
    path: "/support/contacts/overview",
    name: "supportContacts-overview",
    component: () =>
      import("@/views/phases/4_support/contacts/SupportContacts_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Customer Contacts",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Retail Success", to: "/support" },
        { text: "Customer Contacts" },
      ],
    },
  },
  // Promotions
  {
    path: "/promotions/overview",
    name: "promotions-overview",
    component: () =>
      import("@/views/phases/4_support/promotions/Promotions_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Promotions",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Promotions: Overview" },
      ],
    },
  },
  {
    path: "/promotion/new",
    name: "promotions-new",
    component: () =>
      import("@/views/phases/4_support/promotions/Promotion_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Promotion",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Promotions", to: "/promotions/overview" },
        { text: "New Promotion" },
      ],
    },
  },
  {
    path: "/promotion/:id",
    name: "promotions-view",
    component: () =>
      import("@/views/phases/4_support/promotions/Promotion_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Promotion",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Promotions", to: "/promotions/overview" },
        { text: `Promotion` },
      ],
    },
  },
  // Store Events
  {
    path: "/storeEvents/overview",
    name: "storeEvents-overview",
    component: () =>
      import("@/views/phases/4_support/storeEvents/StoreEvents_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Store Events: Overview",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Store Events: Overview" },
      ],
    },
  },
  {
    path: "/storeEvent/new",
    name: "storeEvents-new",
    component: () =>
      import("@/views/phases/4_support/storeEvents/StoreEvent_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Store Event",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Store Events", to: "/storeEvents/overview" },
        { text: "New Event" },
      ],
    },
  },
  {
    path: "/storeEvent/:id",
    name: "storeEvents-view",
    component: () =>
      import("@/views/phases/4_support/storeEvents/StoreEvent_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Store Event",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Store Events", to: "/storeEvents/overview" },
        { text: `Event` },
      ],
    },
  },
  // Trainings
  {
    path: "/trainings/overview",
    name: "budtenderTrainings-overview",
    component: () =>
      import(
        "@/views/phases/4_support/budtenderTraining/BudtenderTraining_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Training Overview",
      trail: () => [{ text: "Home", to: "/" }, { text: "Trainings: Overview" }],
    },
  },
  {
    path: "/training/new",
    name: "budtenderTrainings-new",
    component: () =>
      import(
        "@/views/phases/4_support/budtenderTraining/BudtenderTraining_New.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "New Training",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trainings", to: "/trainings/overview" },
        { text: "New Training" },
      ],
    },
  },
  {
    path: "/training/:id",
    name: "budtenderTrainings-view",
    component: () =>
      import(
        "@/views/phases/4_support/budtenderTraining/BudtenderTraining_View.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Training",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Trainings", to: "/trainings/overview" },
        { text: `Training` },
      ],
    },
  },
  // Support Tickets
  {
    path: "/supportTickets/overview",
    name: "supportTickets-overview",
    component: () =>
      import(
        "@/views/phases/4_support/supportTickets/SupportTickets_Overview.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Support Tickets",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Support Tickets: Overview" },
      ],
    },
  },
  {
    path: "/supportTicket/new",
    name: "supportTickets-new",
    component: () =>
      import("@/views/phases/4_support/supportTickets/SupportTicket_New.vue"),
    meta: {
      requiresAuth: true,
      text: "New Support Ticket",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Support Tickets", to: "/supportTickets/overview" },
        { text: "New Ticket" },
      ],
    },
  },
  {
    path: "/supportTicket/:id",
    name: "supportTickets-view",
    name: "supportTickets-view",
    component: () =>
      import("@/views/phases/4_support/supportTickets/SupportTicket_View.vue"),
    meta: {
      requiresAuth: true,
      text: "Support Ticket",
      trail: (route) => [
        { text: "Home", to: "/" },
        { text: "Support Tickets", to: "/supportTickets/overview" },
        { text: `Ticket` },
      ],
    },
  },
  /* Phase 5 - Reporting & Analytics */
  {
    path: "/reporting/overview",
    name: "reports-overview",
    component: () => import("@/views/phases/5_reporting/Reports_Overview.vue"),
    meta: {
      requiresAuth: true,
      text: "Reports",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports: Overview" },
      ],
    },
  },
  {
    path: "/reporting/admin/tasks",
    name: "admin-tasks-report",
    component: () =>
      import("@/views/phases/5_reporting/0_admin/AdminTasks_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Admin Tasks Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Admin, Tasks" },
      ],
    },
  },
  // {
  //   path: "/reporting/admin/expenses",
  //   name: "admin-marketingExpenses-report",
  //   component: () =>
  //     import("@/views/phases/5_reporting/0_admin/AdminExpenses_Report.vue"),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Admin Marketing Expenses Report",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Trace Reports", to: "/reporting/overview" },
  //       { text: "Marketing Expenses" },
  //     ],
  //   },
  // },
  {
    path: "/reporting/admin/projects",
    name: "admin-projects-report",
    component: () =>
      import("@/views/phases/5_reporting/0_admin/AdminProjects_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Admin Projects Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Admin, Projects" },
      ],
    },
  },
  {
    path: "/reporting/admin/repProductivity",
    name: "admin-repProductivity-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/0_admin/AdminRepProductivity_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Admin Rep Productivity Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Admin, Rep Productivity" },
      ],
    },
  },
  {
    path: "/reporting/admin/notifications",
    name: "admin-notifications-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/0_admin/AdminNotifications_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Admin Notifications Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Admin, Notifications" },
      ],
    },
  },
  {
    path: "/reporting/admin/futures",
    name: "admin-futures-report",
    component: () =>
      import("@/views/phases/5_reporting/0_admin/AdminFutures_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Admin Futures Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Admin, Futures" },
      ],
    },
  },
  {
    path: "/reporting/admin/sales",
    name: "admin-sales-report",
    component: () =>
      import("@/views/phases/5_reporting/0_admin/AdminSales_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Admin Sales Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Admin, Sales" },
      ],
    },
  },
  {
    path: "/reporting/admin/reconciles",
    name: "admin-reconciles-report",
    component: () =>
      import("@/views/phases/5_reporting/0_admin/AdminReconciles_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Admin Reconciles Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Admin, Reconciles" },
      ],
    },
  },

  {
    path: "/reporting/sourcing/componentOrders",
    name: "sourcing-componentOrders-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/1_sourcing/SourcingComponentOrders_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sourcing Vendors Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Component Orders" },
      ],
    },
  },
  // {
  //   path: "/reporting/sourcing/vendors",
  //   name: "sourcing-vendors-report",
  //   component: () =>
  //     import(
  //       "@/views/phases/5_reporting/1_sourcing/SourcingVendors_Report.vue"
  //     ),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Sourcing Vendors Report",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Trace Reports", to: "/reporting/overview" },
  //       { text: "Sourcing, Vendors" },
  //     ],
  //   },
  // },
  // {
  //   path: "/reporting/sourcing/customProductCategories",
  //   name: "sourcing-customProductCategories-report",
  //   component: () =>
  //     import(
  //       "@/views/phases/5_reporting/1_sourcing/SourcingCustomProductCategories_Report.vue"
  //     ),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Sourcing Custom Product Categories Report",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Trace Reports", to: "/reporting/overview" },
  //       { text: "Sourcing, Custom Product Categories" },
  //     ],
  //   },
  // },
  // {
  //   path: "/reporting/sourcing/orders",
  //   name: "sourcing-orders-report",
  //   component: () =>
  //     import("@/views/phases/5_reporting/1_sourcing/SourcingOrders_Report.vue"),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Sourcing Orders Report",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Trace Reports", to: "/reporting/overview" },
  //       { text: "Sourcing, Vendor Orders" },
  //     ],
  //   },
  // },
  // {
  //   path: "/reporting/sourcing/deposits",
  //   name: "sourcing-deposits-report",
  //   component: () =>
  //     import(
  //       "@/views/phases/5_reporting/1_sourcing/SourcingDeposits_Report.vue"
  //     ),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Sourcing Deposits Report",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Trace Reports", to: "/reporting/overview" },
  //       { text: "Sourcing, Deposits" },
  //     ],
  //   },
  // },
  // {
  //   path: "/reporting/sourcing/shipments",
  //   name: "sourcing-shipments-report",
  //   component: () =>
  //     import(
  //       "@/views/phases/5_reporting/1_sourcing/SourcingShipments_Report.vue"
  //     ),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Sourcing Shipments Report",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Trace Reports", to: "/reporting/overview" },
  //       { text: "Souricng, Shipments" },
  //     ],
  //   },
  // },
  {
    path: "/reporting/manufacturing/workOrders",
    name: "manufacturing-workOrders-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/WorkOrders_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Manufacturing Work Orders Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Manufacturing, Work Orders" },
      ],
    },
  },
  {
    path: "/reporting/manufacturing/workOrdersCogs",
    name: "manufacturing-workOrderCogs-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/WorkOrderCogs_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Manufacturing Work Orders Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Work Order - COGs" },
      ],
    },
  },
  {
    path: "/reporting/manufacturing/laborAllocation",
    name: "manufacturing-laborAllocation-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/WorkOrderLabor_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Labor Allocation Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Labor Allocation" },
      ],
    },
  },
  {
    path: "/reporting/manufacturing/inventory",
    name: "manufacturing-inventory-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/LocationInventory_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Manufacturing Inventory Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Manufacturing, Inventory" },
      ],
    },
  },
  {
    path: "/reporting/manufacturing/rawMaterialOrders",
    name: "manufacturing-rawMaterialOrders-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/RawMaterialOrders_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Manufacturing Raw Material Orders Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Manufacturing, Raw Material Orders" },
      ],
    },
  },
  {
    path: "/reporting/manufacturing/productLots",
    name: "manufacturing-productLots-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/ProductLotManagement_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Manufacturing Product Batches Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Manufacturing, Product Batches" },
      ],
    },
  },
  {
    path: "/reporting/manufacturing/inventoryActivity",
    name: "manufacturing-inventoryActivity-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/InventoryActivity_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "SKU Inventory Activity Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Manufacturing, Inventory Activity" },
      ],
    },
  },
  {
    path: "/reporting/manufacturing/inventoryActivityII",
    name: "manufacturing-inventoryActivityII-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/InventoryActivity_ReportII.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "SKU Inventory Activity Report II",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Manufacturing, Inventory Activity II" },
      ],
    },
  },
  {
    path: "/reporting/manufacturing/laborv2",
    name: "manufacturing-laborv2-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/LaborV2_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Labor V2",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Manufacturing, Labor V2" },
      ],
    },
  },
  {
    path: "/reporting/manufacturing/componentinventory",
    name: "manufacturing-componentinventory-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/2_manufacturing/InventoryComponents_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Component Inventory",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Component Inventory" },
      ],
    },
  },
  {
    path: "/reporting/sales/leaderboard",
    name: "sales-leaderboard-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesLeaderboard_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Leaderboard Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Leaderboard" },
      ],
    },
  },
  {
    path: "/reporting/sales/score",
    name: "sales-score-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesRepScore_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Rep Score Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Rep Score" },
      ],
    },
  },
  {
    path: "/reporting/sales/accounts",
    name: "sales-accounts-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesAccounts_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Accounts Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Accounts" },
      ],
    },
  },
  {
    path: "/reporting/sales/map",
    name: "sales-map-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesMap_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Map Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Account Map" },
      ],
    },
  },
  {
    path: "/reporting/sales/accountPricing",
    name: "sales-accountPricing-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/3_sales/SalesAccountPricing_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sales Account Pricing Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Account Pricing" },
      ],
    },
  },
  {
    path: "/reporting/sales/accountMetrics",
    name: "sales-accountMetrics-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/3_sales/SalesAccountMetrics_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sales Account Metrics Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Account Metrics" },
      ],
    },
  },
  {
    path: "/reporting/sales/closes",
    name: "sales-closes-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesCloses_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Closes Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Closes" },
      ],
    },
  },
  {
    path: "/reporting/sales/onboarding",
    name: "sales-onboardings-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/3_sales/SalesAccountOnboarding_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sales Account Onboarding Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Account Onboarding" },
      ],
    },
  },
  // {
  //   path: "/reporting/sales/actionLists",
  //   name: "sales-actionLists-report",
  //   component: () =>
  //     import("@/views/phases/5_reporting/3_sales/SalesActionLists_Report.vue"),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Sales Action Lists Report",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Trace Reports", to: "/reporting/overview" },
  //       { text: "Sales, Action Lists" },
  //     ],
  //   },
  // },
  {
    path: "/reporting/sales/tasks",
    name: "sales-tasks-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesTasks_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Tasks Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Tasks" },
      ],
    },
  },
  {
    path: "/reporting/sales/mtd",
    name: "sales-brandmtd-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesMTDSales_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales MTD Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, MTD" },
      ],
    },
  },
  {
    path: "/reporting/sales/orders",
    name: "sales-orders-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesOrders_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Orders Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Orders" },
      ],
    },
  },
  {
    path: "/reporting/sales/sampleOrders",
    name: "sales-sampleOrders-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesSampleOrders_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Sample Orders Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Sample Orders" },
      ],
    },
  },
  {
    path: "/reporting/sales/traceOrders",
    name: "sales-traceOrders-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesTraceOrders_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Trace Orders Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Trace Orders" },
      ],
    },
  },
  {
    path: "/reporting/sales/ordersByAccount",
    name: "sales-ordersByAccount-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/3_sales/SalesOrdersByAccount_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sales Orders By Account Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Orders By Account" },
      ],
    },
  },
  {
    path: "/reporting/sales/postDiscounts",
    name: "sales-postDiscount-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/3_sales/SalesPostDiscountOrders_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sales Post Discount Orders Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Post Discount Orders" },
      ],
    },
  },
  {
    path: "/reporting/sales/products",
    name: "sales-products-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesProducts_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Products Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales by Product" },
      ],
    },
  },
  {
    path: "/reporting/sales/firstPurchase",
    name: "sales-firstPurchase-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/3_sales/SalesFirstPurchase_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sales 1st Purchase Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "1st Purchase" },
      ],
    },
  },
  {
    path: "/reporting/sales/productTrends",
    name: "sales-topProducts-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesTopProducts_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Product Trends Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Product Trends" },
      ],
    },
  },
  {
    path: "/reporting/sales/productCategories",
    name: "sales-productCategories-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/3_sales/SalesProductCategories_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sales By product categories Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Product Categories" },
      ],
    },
  },
  {
    path: "/reporting/sales/deliveryLogistics",
    name: "sales-deliveryLogistics-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/3_sales/SalesDeliveryLogistics_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sales Delivery Logistics Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Delivery Logistics" },
      ],
    },
  },
  {
    path: "/reporting/sales/ar",
    name: "sales-ar-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesAR_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales AR Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Accounts Receivable (AR)" },
      ],
    },
  },
  {
    path: "/reporting/sales/payments",
    name: "sales-payments-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesPayments_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Payments Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Payments" },
      ],
    },
  },
  {
    path: "/reporting/sales/bonus",
    name: "sales-bonus-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesBonus_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Bonus Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Bonus" },
      ],
    },
  },
  {
    path: "/reporting/sales/credits",
    name: "sales-credits-report",
    component: () =>
      import("@/views/phases/5_reporting/3_sales/SalesCredits_Report.vue"),
    meta: {
      requiresAuth: true,
      text: "Sales Credits Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Credits" },
      ],
    },
  },
  {
    path: "/reporting/sales/creditsVsSales",
    name: "sales-creditsVsSales-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/3_sales/SalesCreditsVsSales_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Sales Credits Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Sales, Credits vs. Sales" },
      ],
    },
  },
  // {
  //   path: "/reporting/sales/tpva",
  //   name: "sales-tpva-report",
  //   component: () =>
  //     import("@/views/phases/5_reporting/3_sales/SalesTPVA_Report.vue"),
  //   meta: {
  //     requiresAuth: true,
  //     text: "Sales TPVA Report",
  //     trail: () => [
  //       { text: "Home", to: "/" },
  //       { text: "Trace Reports", to: "/reporting/overview" },
  //       { text: "Sales, TPVA" },
  //     ],
  //   },
  // },
  {
    path: "/reporting/support/storeEvents",
    name: "support-storeEvents-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/4_support/Support_StoreEvents_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Support Store Events Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Store Events, Report" },
      ],
    },
  },
  {
    path: "/reporting/support/trainings",
    name: "support-trainings-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/4_support/Support_Trainings_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Support Trainings Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Retail Success, Trainings" },
      ],
    },
  },
  {
    path: "/reporting/support/supportTickets",
    name: "support-supportTickets-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/4_support/Support_SupportTickets_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Support Tickets Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Retail Success, Support Tickets" },
      ],
    },
  },
  {
    path: "/reporting/sales/deliveryCogs",
    name: "support-deliveryCogs-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/4_support/Support_DeliveryCogs_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Support Delivery COGS Report",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Delivery COGS" },
      ],
    },
  },
  // External
  {
    path: "/reporting/leaflink/salesOrders",
    name: "leaflink-salesOrders-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/_external/leaflink/LeafLink_SalesOrderComparison_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "LeafLink: Sales Order Comparison",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "LeafLink: Sales Order comparison" },
      ],
    },
  },
  {
    path: "/reporting/flourish/salesOrders",
    name: "flourish-salesOrders-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/_external/flourish/Flourish_SalesOrders_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Flourish: Sales Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Flourish: Sales Orders" },
      ],
    },
  },
  {
    path: "/reporting/nabis/creditRatings",
    name: "nabis-creditRatings-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/_external/nabis/Nabis_CreditRatings_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Nabis: Credit Ratings",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Nabis: Credit Ratings" },
      ],
    },
  },
  {
    path: "/reporting/nabis/inventory",
    name: "nabis-inventory-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/_external/nabis/Nabis_Inventory_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Nabis: Inventory",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Nabis: Inventory" },
      ],
    },
  },
  {
    path: "/reporting/nabis/orders",
    name: "nabis-orders-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/_external/nabis/Nabis_Orders_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Nabis: Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Nabis: Orders" },
      ],
    },
  },
  {
    path: "/reporting/gemini/salesOrders",
    name: "gemini-salesOrders-report",
    component: () =>
      import(
        "@/views/phases/5_reporting/_external/gemini/Gemini_SalesOrders_Report.vue"
      ),
    meta: {
      requiresAuth: true,
      text: "Gemini: Sales Orders",
      trail: () => [
        { text: "Home", to: "/" },
        { text: "Trace Reports", to: "/reporting/overview" },
        { text: "Gemini: Sales Orders" },
      ],
    },
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (auth.currentUser) {
      // Support both old string[] and new object[] view formats
      const rawViews = store.getters.getUser.views || [];
      const userViewValues = rawViews.map((v) => (typeof v === "string" ? v : v.value));

      let userViews = store.getters.getPhaseViews
        .filter((phaseView) =>
          userViewValues.includes(phaseView.value),
        )
        .flatMap((phaseView) => phaseView.views);

      let phaseRoutes = store.getters.getPhases
        .filter(
          (phase) =>
            phase.route !== null &&
            userViewValues.includes(phase.routeValue),
        )
        .flatMap((phase) => phase.views);

      let defaultViews = [
        "home",
        "profile-view",
        "contacts-new",
        "contacts-view",
        "location-rawMaterials-overview",
        "location-componentBatches-overview",
        "saleOrders-overview",
        "salesOrder-new",
        "salesOrder-view",
        "promotionCredit-view",
        "storeEvents-view",
        "sales-creditsVsSales-report",
        "reports-overview",
      ];

      // if (['accountExecutive', 'brandAmbassador', 'accountManager'].includes(store.getters.getUser.role)) {
      //   defaultViews.push('storeEvents-view')
      // }

      let authorizedViews = userViews.concat(phaseRoutes).concat(defaultViews);

      // Check if this is a report route
      const allReports = store.getters.getReports || [];
      const isReportRoute = allReports.some((report) => report.to === to.name);

      // Check if user has access to report routes via their reports array
      const userReports = store.getters.getUser.reports;

      let hasReportAccess = false;
      if (isReportRoute) {
        const isAdmin = store.getters.getUser.accountType >= 4;
        if (isAdmin) {
          hasReportAccess = true; // Admin: full access
        } else if (userReports && userReports.length > 0) {
          hasReportAccess = userReports.includes(to.name); // Non-admin: only assigned reports
        }
        // Non-admin with no/empty reports array: hasReportAccess stays false → 403
      }

      if (
        store.getters.getUser.accountType >= 4 ||
        authorizedViews.includes(to.name) ||
        hasReportAccess
      ) {
        next();
      } else {
        next({
          name: "403",
        });
      }
    } else {
      next({
        path: "/login",
      });
    }
  } else {
    next();
  }
});

export default router;
